import * as React from 'react';
import { connect } from 'react-redux';
import { uniqBy, findIndex, merge, sortBy } from 'lodash';
import { Button, ButtonGroup, Icon, AccessoryList } from '../';

interface Props {
  booking: any;
  duration: number;
  isDisabled?: boolean;
  handleUpdate: Function;
  accessories: Array<any>;
  list: boolean;
}

interface State {
  accessories: Array<any>;
  accessoryCost: number;
  isAddingAccessory: boolean;
}

class BookingAccessories extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      accessories: uniqBy(
        [
          ...this.props.booking.accessories,
          ...this.props.accessories.map((accessory) =>
            merge(accessory, { quantity: 0, total: 0 }),
          ),
        ],
        'id',
      ).sort((a, b) => {
        if (a.featured) {
          return -1;
        }
        if (b.featured) {
          return 1;
        }
        return 0;
      }),
      accessoryCost: this.props.booking.accessory_cost,
      isAddingAccessory: false,
    };
    this.renderAccessory = this.renderAccessory.bind(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.duration !== this.props.duration) {
      this.state.accessories.forEach((accessory) =>
        this.setAccessoryQuantity(
          accessory,
          accessory.quantity,
          nextProps.duration,
        ),
      );
    }
  }

  /**
   * Recalculate the accessory totals
   * @param {Array<Accessory>} accessories
   * @param {Accessory} accessory
   * @param {Number} quantity
   * @param {Number} duration
   * @param {Number} driverCount
   */
  public static calculateAccessoryQuantity(
    accessories: Array<any>,
    accessory: any,
    quantity: number,
    duration: number,
    driverCount: number,
  ) {
    const accessoryIndex = findIndex(accessories, { id: accessory.id });

    // Update quantity
    accessory.quantity = quantity;
    accessory.quantity = Math.max(0, accessory.quantity);
    if (accessory.limit > 0) {
      accessory.quantity = Math.min(accessory.limit, accessory.quantity);
    }

    // Recalculate total
    accessory.total = Number(accessory.price) * accessory.quantity;

    if (accessory.per_day) {
      accessory.total *= duration;
    }
    if (accessory.per_driver) {
      accessory.total *= driverCount;
    }

    accessory.total +=
      Number(accessory.additional_weekly_price) *
      ~~(duration / 7) *
      accessory.quantity;

    if (duration > 0) {
      accessory.total +=
        Number(accessory.additional_daily_price) * (duration - 1);
    }

    // Save changes
    accessories[accessoryIndex] = accessory;
  }

  /**
   * Set a new quantity for an accessory and recalculate totals
   * @param {Object} accessory
   * @param {Number} quantity
   */
  setAccessoryQuantity(accessory, quantity, duration = this.props.duration) {
    const accessories = this.state.accessories;
    BookingAccessories.calculateAccessoryQuantity(
      accessories,
      accessory,
      quantity,
      duration,
      this.props.booking.drivers.length,
    );
    this.setState({ accessories }, () => {
      this.recalculateAccessoryTotal();
    });
  }

  /**
   * Recalculate the total accessory cost
   */
  recalculateAccessoryTotal() {
    let accessoryCost = 0;
    this.state.accessories.forEach((a) => {
      accessoryCost += a.total;
    });
    this.setState({ accessoryCost }, () => {
      this.props.handleUpdate(
        this.state.accessories.filter(({ quantity }) => quantity),
        accessoryCost,
      );
    });
  }

  renderHead() {
    return null;
  }

  /**
   * Sub-section for managing accessory quantities
   * @param {Object} accessory
   */
  renderAccessory(accessory: any) {
    let tooltip = `per accessory`;
    if (accessory.per_day) {
      tooltip += ', per day';
    }
    if (accessory.per_driver) {
      tooltip += ', per driver';
    }
    tooltip = `£${accessory.price.toFixed(2)} ${tooltip}`;
    if (this.props.duration >= 7 && accessory.additional_weekly_price > 0) {
      tooltip += ` - £${accessory.additional_weekly_price.toFixed(2)} per week`;
    }

    return (
      <div>
        <div className="level is-mobile">
          <div className="level-left">
            <div className="level-item">
              <p>
                <strong>
                  <span>{accessory.name}</span>
                  {accessory.featured && (
                    <span className="has-text-primary">
                      <Icon icon="star" />
                    </span>
                  )}
                </strong>
                <br />
                {tooltip}
              </p>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              {!this.props.isDisabled && (
                <div style={{ marginTop: '0.5em' }}>
                  <ButtonGroup key="controls">
                    <span className="button is-static">
                      {accessory.quantity} for £{accessory.total.toFixed(2)}
                    </span>
                    <Button
                      className="is-default"
                      handleClick={() => {
                        this.setAccessoryQuantity(
                          accessory,
                          accessory.quantity + 1,
                        );
                      }}
                    >
                      <Icon icon="plus-square" />
                    </Button>
                    <Button
                      className="is-default"
                      handleClick={() => {
                        this.setAccessoryQuantity(
                          accessory,
                          accessory.quantity - 1,
                        );
                      }}
                    >
                      <Icon icon="minus-square" />
                    </Button>
                  </ButtonGroup>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <AccessoryList
        hideSearch
        renderHead={this.renderHead}
        items={this.state.accessories}
        showJoinDate={false}
        renderBody={this.renderAccessory}
        handleClick={() => {}}
      />
    );
  }
}

const mapStateToProps = ({ accessories }) => ({
  accessories: sortBy(accessories.accessories, 'name'),
});

export default connect(mapStateToProps)(BookingAccessories);
